import { useState, useEffect, useRef } from 'react';
import { Link, useMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function FloatingContact() {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const tourMatch = useMatch('/tour/:id');
  const tourId = tourMatch?.params?.id;

  const contactOptions = [
    {
      id: 'call',
      label: t('floating.callUs'),
      sub: '+84 362 923 218',
      icon: 'ri-phone-line',
      href: 'tel:+84362923218',
      color: 'bg-primary-500 hover:bg-primary-600',
    },
    {
      id: 'email',
      label: t('floating.email'),
      sub: 'admin@vietnam.st',
      icon: 'ri-mail-line',
      href: 'mailto:admin@vietnam.st',
      color: 'bg-accent-500 hover:bg-accent-600',
    },
    {
      id: 'whatsapp',
      label: t('floating.whatsapp'),
      sub: t('floating.chatInstantly'),
      icon: 'ri-whatsapp-line',
      href: 'https://wa.me/84362923218',
      color: 'bg-green-600 hover:bg-green-700',
    },
    {
      id: 'messenger',
      label: t('floating.messenger'),
      sub: t('floating.fbChat'),
      icon: 'ri-messenger-line',
      href: 'https://m.me/vietnamstory',
      color: 'bg-secondary-500 hover:bg-secondary-600',
    },
  ];

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={wrapRef} className="fixed right-0 top-1/2 -translate-y-1/2 z-[60] flex flex-col items-end gap-3 pointer-events-none">
      {/* Options list */}
      <div
        className={`flex flex-col items-end gap-3 transition-all duration-300 ${
          open ? 'opacity-100 translate-x-0 pointer-events-auto' : 'opacity-0 translate-x-4 pointer-events-none'
        }`}
      >
        {contactOptions.map((opt) => (
          <a
            key={opt.id}
            href={opt.href}
            target={opt.href.startsWith('http') ? '_blank' : undefined}
            rel={opt.href.startsWith('http') ? 'noreferrer noopener' : undefined}
            className="group flex items-center gap-3"
          >
            <span className="bg-white border border-background-200 rounded-lg px-3 py-2 shadow-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              <span className="block text-xs font-semibold text-foreground-900">{opt.label}</span>
              <span className="block text-[11px] text-foreground-600">{opt.sub}</span>
            </span>
            <span
              className={`w-11 h-11 ${opt.color} text-white rounded-full flex items-center justify-center shadow-md transition-transform hover:scale-110 pointer-events-auto`}
            >
              <i className={`${opt.icon} text-lg`} />
            </span>
          </a>
        ))}
      </div>

      {/* Main toggle button */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t('floating.quickContact')}
        className="w-14 h-14 bg-primary-500 text-white rounded-l-full flex items-center justify-center shadow-lg hover:bg-primary-600 transition-all cursor-pointer pointer-events-auto"
      >
        <i className={`text-2xl transition-all duration-300 ${open ? 'ri-close-line' : 'ri-chat-3-line'}`} />
      </button>

      {/* Book now button (desktop only, appears only on tour detail page) */}
      {tourId && (
        <Link
          to={`/booking/${tourId}`}
          className="relative hidden lg:flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-white rounded-l-full pl-4 pr-5 py-3 shadow-lg transition-all duration-300 hover:pr-6 cursor-pointer pointer-events-auto"
        >
          <span className="absolute inset-0 rounded-l-full bg-accent-500 opacity-40 animate-ping pointer-events-none" />
          <span className="relative flex items-center gap-2">
            <i className="ri-ticket-2-line text-xl" />
            <span className="text-sm font-bold whitespace-nowrap">{t('floating.bookNow')}</span>
          </span>
        </Link>
      )}
    </div>
  );
}